import { gql } from '@apollo/client';
import { endOfToday, sub } from 'date-fns';
import { useAtomValue } from 'jotai';
import { ComponentProps, useMemo } from 'react';

import { type PreVisitSummaryCardConnected } from '@grow-therapy-team/seedling-components';

import { Server } from '../../../apollo/types';
import { useAppDependenciesContext } from '../../../state';
import { scheduledPatientInformationAtom } from '../../schedule-preview/state';
import { AppointmentType } from '../../schedule-preview/types';
import { isInSessionWithSelectedClientAtom, providerShortIdAtom, selectedClientInfoAtom } from '../../state';
import { PreVisitSummaryCardNudgeWrapper } from './PreVisitSummaryCardNudgeWrapper';
import {
  PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQuery,
  PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQueryVariables,
} from './types';
import { useResourceButtonOnClickHandlers } from './useResourceButtonOnClickHandlers';

const TIME_START_LOWER_BOUND_MINUTES = 15;

type ViewModel = Partial<
  Pick<
    ComponentProps<typeof PreVisitSummaryCardConnected>,
    'appointmentShortId' | 'patientShortId' | 'nudgeContext' | 'preVisitSummaryCardComponent'
  >
> &
  Pick<ComponentProps<typeof PreVisitSummaryCardConnected>, 'resourceButtonOnClickHandlers'> & {
    appointmentType?: AppointmentType | string;
  };

/**
 * Intended to query for the selected patient's upcoming appointment's short ID.
 */
export const PRE_VISIT_SUMMARY_CARD_WRAPPER__GET_UPCOMING_APPOINTMENT_FOR_PATIENT_QUERY = gql`
  query PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatient($filters: AppointmentsFilter!) {
    allAppointments(filters: $filters) {
      edges {
        node {
          id
          appointmentShortId
          appointmentType
        }
      }
    }
  }
`;

function getFifteenMinutesAgoISO() {
  return sub(new Date(), {
    minutes: TIME_START_LOWER_BOUND_MINUTES,
  }).toISOString();
}

export function usePreVisitSummaryCardWrapper(): ViewModel {
  const isInSessionWithSelectedClient = useAtomValue(isInSessionWithSelectedClientAtom);
  const selectedPatientInfo = useAtomValue(selectedClientInfoAtom);
  const scheduledPatientInfo = useAtomValue(scheduledPatientInformationAtom);
  const selectedAppointmentShortId =
    selectedPatientInfo?.inSessionAppointmentShortId || scheduledPatientInfo?.appointmentShortId;
  const patientShortId = selectedPatientInfo?.patientShortId || scheduledPatientInfo?.patientShortId || '';
  const providerShortId = useAtomValue(providerShortIdAtom);
  const { useQuery } = useAppDependenciesContext();

  const alreadyHasAppointmentShortId = !!selectedAppointmentShortId;

  const fifteenMinutesAgoISO = useMemo(getFifteenMinutesAgoISO, []);

  const { data } = useQuery<
    PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQuery,
    PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQueryVariables
  >(PRE_VISIT_SUMMARY_CARD_WRAPPER__GET_UPCOMING_APPOINTMENT_FOR_PATIENT_QUERY, {
    skip: !!alreadyHasAppointmentShortId || !patientShortId,
    variables: {
      filters: {
        patientShortIds: [patientShortId],
        timeStartRange: {
          begin: fifteenMinutesAgoISO,
          end: endOfToday().toISOString(),
        },
        canceled: false,
      },
    },
    context: { server: Server.MONOLITH },
  });

  const upcomingAppointment = data?.allAppointments?.edges?.[0]?.node;
  const upcomingAppointmentShortId = upcomingAppointment?.appointmentShortId;

  const appointmentShortId = selectedAppointmentShortId || upcomingAppointmentShortId;
  const appointmentType = scheduledPatientInfo?.appointmentType || upcomingAppointment?.appointmentType;

  const resourceButtonOnClickHandlers = useResourceButtonOnClickHandlers({
    appointmentShortId,
    patientShortId,
    providerShortId,
  });

  return {
    appointmentShortId,
    patientShortId,
    appointmentType,
    nudgeContext: isInSessionWithSelectedClient ? 'telehealth-session' : 'telehealth-waiting-room',
    preVisitSummaryCardComponent: PreVisitSummaryCardNudgeWrapper,
    resourceButtonOnClickHandlers,
  };
}
